import React, { useState, useEffect } from 'react';
import { Card, Container, Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { mockStores } from "../../data/stores";
import { dashboardAPI } from "../../services/api";
import BreadCrumb from "../../Common/BreadCrumb";

const Settings = () => {
    const { t } = useTranslation();
    document.title = t('Settings.title');
    
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [dateFormat, setDateFormat] = useState('dd.MM.yyyy');
    const [timezone, setTimezone] = useState('Europe/Istanbul');
    const [defaultStoreId, setDefaultStoreId] = useState<string | null>(null);
    const [stores, setStores] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Check if demo user
    const isDemoUser = () => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token && typeof token === 'string' && token.startsWith('demo-token-');
        } catch {
            return false;
        }
    };

    // Fetch settings and stores on load
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                // Fetch stores
                const storesResponse: any = await dashboardAPI.getStores();
                const storesData = Array.isArray(storesResponse) ? storesResponse : (storesResponse?.data || []);
                setStores(storesData);

                // Fetch settings (only for real users)
                if (!isDemoUser()) {
                    const settings: any = await dashboardAPI.getSettings();
                    if (settings) {
                        setEmailNotifications(settings.emailNotifications !== undefined ? settings.emailNotifications : true);
                        setDateFormat(settings.dateFormat || 'dd.MM.yyyy');
                        setTimezone(settings.timezone || 'Europe/Istanbul');
                        setDefaultStoreId(settings.defaultStoreId || null);
                    }
                } else {
                    // Demo user: use mock stores
                    setStores(mockStores);
                }
            } catch (err: any) {
                console.error('Failed to fetch settings:', err);
                setError(err?.response?.data?.message || err?.message || t('Common.error_loading_data') || 'Failed to load settings');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [t]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            const payload = {
                emailNotifications,
                dateFormat,
                timezone,
                defaultStoreId: defaultStoreId || null,
            };

            await dashboardAPI.saveSettings(payload);
            setSuccess(true);
            
            // Hide success message after 3 seconds
            setTimeout(() => setSuccess(false), 3000);
        } catch (err: any) {
            console.error('Failed to save settings:', err);
            setError(err?.response?.data?.message || err?.message || t('Settings.save_error') || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Settings.title'), path: "/settings"}
                        ]}
                    />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <Card.Header>
                                    <h5 className="mb-0">{t('Settings.title')}</h5>
                                </Card.Header>
                                <Card.Body>
                                    {loading ? (
                                        <div className="text-center p-4">
                                            <Spinner animation="border" role="status">
                                                <span className="visually-hidden">{t('Common.loading')}</span>
                                            </Spinner>
                                            <p className="mt-2">{t('Common.loading_data')}</p>
                                        </div>
                                    ) : (
                                        <>
                                            {error && (
                                                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                                    {error}
                                                </Alert>
                                            )}
                                            
                                            {success && (
                                                <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
                                                    {t('Settings.save_success') || 'Settings saved successfully!'}
                                                </Alert>
                                            )}

                                            <Form>
                                                <Form.Group className="mb-3" controlId="emailNotifications">
                                                    <Form.Check
                                                        type="switch"
                                                        label={t('Settings.email_notifications')}
                                                        checked={emailNotifications}
                                                        onChange={(e) => setEmailNotifications(e.target.checked)}
                                                        disabled={saving}
                                                    />
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="dateFormat">
                                                    <Form.Label>{t('Settings.date_format')}</Form.Label>
                                                    <Form.Select 
                                                        value={dateFormat} 
                                                        onChange={(e) => setDateFormat(e.target.value)}
                                                        disabled={saving}
                                                    >
                                                        <option value="dd.MM.yyyy">31.03.2025</option>
                                                        <option value="MM/dd/yyyy">03/31/2025</option>
                                                        <option value="yyyy-MM-dd">2025-03-31</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="timezone">
                                                    <Form.Label>{t('Settings.timezone')}</Form.Label>
                                                    <Form.Select 
                                                        value={timezone} 
                                                        onChange={(e) => setTimezone(e.target.value)}
                                                        disabled={saving}
                                                    >
                                                        <option value="Europe/Istanbul">Istanbul (UTC +3)</option>
                                                        <option value="Europe/Berlin">Almanya (UTC +1)</option>
                                                        <option value="UTC">UTC</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group className="mb-4" controlId="defaultStore">
                                                    <Form.Label>{t('Settings.default_store')}</Form.Label>
                                                    <Form.Select 
                                                        value={defaultStoreId || ''} 
                                                        onChange={(e) => setDefaultStoreId(e.target.value || null)}
                                                        disabled={saving}
                                                    >
                                                        <option value="">{t('Settings.no_default_store') || 'No default store'}</option>
                                                        {stores.map((store) => (
                                                            <option key={store.id} value={store.id}>
                                                                {store.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>

                                                <div className="text-end">
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={handleSave}
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                {t('Settings.saving') || 'Saving...'}
                                                            </>
                                                        ) : (
                                                            t('Settings.save')
                                                        )}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>

    );
};

export default Settings;
